import type { Midia } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Snapshot do instante atual em strings ISO locais
// ─────────────────────────────────────────────────────────────────────────────

export interface NowSnapshot {
  /** 'YYYY-MM-DD' — comparável diretamente com data_inicio/data_fim */
  dateStr: string;
  /** 'HH:MM:SS'   — comparável diretamente com hora_inicio/hora_fim */
  timeStr: string;
  /** 0 = Domingo … 6 = Sábado (igual ao getDay() do JS) */
  weekDay: number;
}

/**
 * Constrói um NowSnapshot a partir de uma Date.
 * Usa o horário LOCAL do dispositivo — correto para kiosks/TVs em restaurantes.
 */
export function getNowSnapshot(date: Date = new Date()): NowSnapshot {
  return {
    dateStr: [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-'),
    timeStr: [
      String(date.getHours()).padStart(2, '0'),
      String(date.getMinutes()).padStart(2, '0'),
      String(date.getSeconds()).padStart(2, '0'),
    ].join(':'),
    weekDay: date.getDay(),
  };
}

/** Data e dia da semana do dia anterior a `now` (virada de mês/ano incluída). */
function diaAnterior(now: NowSnapshot): Pick<NowSnapshot, 'dateStr' | 'weekDay'> {
  const [ano, mes, dia] = now.dateStr.split('-').map(Number);
  const anterior = new Date(ano, mes - 1, dia - 1);
  const { dateStr, weekDay } = getNowSnapshot(anterior);
  return { dateStr, weekDay };
}

// ─────────────────────────────────────────────────────────────────────────────
// Função pura de validação
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Retorna `true` se a mídia deve ser exibida no instante `now`.
 *
 * Regras (todas AND, todas opcionais):
 *
 *  ① data_inicio / data_fim  →  faixa de datas sazonal
 *     - Strings 'YYYY-MM-DD' são comparadas lexicograficamente (funciona!)
 *     - data_fim é INCLUSIVO (a mídia ainda exibe neste dia)
 *
 *  ② hora_inicio / hora_fim  →  janela horária diária
 *     - Strings 'HH:MM:SS' são comparadas lexicograficamente
 *     - hora_fim é EXCLUSIVO: mídia exibida das 11:00 até 13:59:59 com hora_fim='14:00:00'
 *     - Janela que cruza a meia-noite (hora_inicio > hora_fim, ex.: 22:00–02:00) vale das 22:00 até
 *       23:59:59 e das 00:00 até 01:59:59. A parte depois da meia-noite pertence ao dia em que a janela
 *       COMEÇOU: as regras ① e ③ olham o dia anterior (sexta 22h–02h continua valendo no sábado 01h).
 *     - hora_inicio === hora_fim é uma janela vazia: nunca exibe
 *
 *  ③ dias_semana  →  array de inteiros [0–6]
 *     - Array vazio ou null = qualquer dia
 *
 * Se todos os campos de agendamento forem null → mídia sempre válida.
 */
export function verificarMidiaValida(
  midia: Midia,
  now: NowSnapshot = getNowSnapshot(),
): boolean {
  const { timeStr } = now;
  const { hora_inicio, hora_fim } = midia;

  // Janela que cruza a meia-noite: depois das 00:00 ela ainda é a do dia anterior.
  const cruzaMeiaNoite = !!hora_inicio && !!hora_fim && hora_inicio > hora_fim;
  const posMeiaNoite   = cruzaMeiaNoite && timeStr < hora_fim!;
  const dia = posMeiaNoite ? diaAnterior(now) : now;

  // ── Regra ①: período sazonal ──────────────────────────────────────────────
  if (midia.data_inicio && dia.dateStr < midia.data_inicio) return false;
  if (midia.data_fim    && dia.dateStr > midia.data_fim)   return false;

  // ── Regra ②: janela horária diária ───────────────────────────────────────
  if (cruzaMeiaNoite) {
    if (timeStr < hora_inicio! && timeStr >= hora_fim!) return false;
  } else {
    if (hora_inicio && timeStr < hora_inicio) return false;
    if (hora_fim    && timeStr >= hora_fim)   return false;
  }

  // ── Regra ③: dias da semana ───────────────────────────────────────────────
  if (midia.dias_semana && midia.dias_semana.length > 0) {
    if (!midia.dias_semana.includes(dia.weekDay)) return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Utilitários de formatação (usados no MediaEditor)
// ─────────────────────────────────────────────────────────────────────────────

const DIAS_NOMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

/** Retorna um texto resumido do agendamento de uma mídia, ou null se sem agendamento */
export function formatAgendamentoBadge(midia: Midia): string | null {
  const parts: string[] = [];

  // Horário
  if (midia.hora_inicio && midia.hora_fim) {
    parts.push(`${midia.hora_inicio.slice(0, 5)}–${midia.hora_fim.slice(0, 5)}`);
  } else if (midia.hora_inicio) {
    parts.push(`A partir das ${midia.hora_inicio.slice(0, 5)}`);
  } else if (midia.hora_fim) {
    parts.push(`Até ${midia.hora_fim.slice(0, 5)}`);
  }

  // Dias da semana
  if (midia.dias_semana && midia.dias_semana.length > 0) {
    const sorted = [...midia.dias_semana].sort((a, b) => a - b);
    parts.push(
      sorted.length <= 3
        ? sorted.map(d => DIAS_NOMES[d]).join('/')
        : `${sorted.length} dias`,
    );
  }

  // Datas sazonais
  if (midia.data_inicio && midia.data_fim) {
    parts.push(`${midia.data_inicio} a ${midia.data_fim}`);
  } else if (midia.data_inicio) {
    parts.push(`De ${midia.data_inicio}`);
  } else if (midia.data_fim) {
    parts.push(`Até ${midia.data_fim}`);
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}
